import React from "react"

import {
  LLMModelSelect,
  RepeatsItselfConfig,
  TranscriptFilterDetails,
  TranscriptFilterToggle,
} from "@/metrics/registry/configuration"
import { supportsTranscriptFilter, type MetricRecord } from "@/metrics/runtime"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { BinaryLLMMetricRecord } from "./BinaryLLMMetricRecord"

interface AgentRepeatsItselfOccurrence {
  start: number
  end: number
  timing_method: string
}

interface AgentRepeatsItselfEvent {
  type: string
  phrase: string
  count: number
  occurrences: AgentRepeatsItselfOccurrence[]
  explanation?: string
}

interface _AgentRepeatsItselfExplanation {
  repeated_phrase_events: AgentRepeatsItselfEvent[]
  explanation?: string
  metadata: {
    duration: number
    count: number
  }
}

export class AgentRepeatsItselfMetricRecord extends BinaryLLMMetricRecord {
  // NO is good for this metric (agent didn't repeat itself)
  invertYesNoColors = true

  renderReadOnlyMetadata(): React.ReactNode {
    return null
  }

  getHeaderValue(mo: RunMetricOutput[]): string | null {
    // % of calls where the agent repeated itself (YES); 0% is healthy.
    return this.getFailureRateHeaderValue(mo)
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    _textAreaRef?: React.RefObject<HTMLTextAreaElement>,
    editorContext?: Parameters<MetricRecord["renderEditorMetadata"]>[3],
  ): React.ReactNode {
    if (editorContext?.readOnly) {
      return null
    }

    const showTranscriptFilter = supportsTranscriptFilter(metricRecordData)

    return (
      <div className="flex flex-col gap-2">
        <RepeatsItselfConfig
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        <LLMModelSelect
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        {showTranscriptFilter && (
          <TranscriptFilterToggle
            metricRecordData={metricRecordData}
            onChange={onChange}
          />
        )}
        {showTranscriptFilter && (
          <TranscriptFilterDetails
            metricRecordData={metricRecordData}
            onChange={onChange}
          />
        )}
      </div>
    )
  }

  renderMetadataField(metricRecordData: MetricRecordData): React.ReactNode {
    const metadata = metricRecordData.metric_metadata

    return (
      <div className="flex flex-col gap-2">
        {metadata?.regex_pattern && (
          <div>
            <h3 className="font-semibold">Regex Pattern:</h3>
            <p className="font-mono">{metadata.regex_pattern}</p>
          </div>
        )}
        <div>
          <h3 className="font-semibold">Similarity Threshold:</h3>
          <p>{metadata?.similarity_threshold ?? 0.85}</p>
        </div>
        <div>
          <h3 className="font-semibold">Min Repetition Count:</h3>
          <p>{metadata?.min_repetition_count ?? 2}</p>
        </div>
        {metadata?.case_insensitive && (
          <div>
            <h3 className="font-semibold">Case Insensitive:</h3>
            <p>Yes</p>
          </div>
        )}
        {metadata?.custom_prompt && (
          <div>
            <h3 className="font-semibold">Custom Prompt:</h3>
            <p className="whitespace-pre-wrap">{metadata.custom_prompt}</p>
          </div>
        )}
      </div>
    )
  }
}
